import api from "../api";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AuthCard from "./authCard";

function AccountInit() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await api.get(`/events/validateInitToken/${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.email);
        } else {
          setTokenValid(false);
          setMessage(response.data.error);
        }
      } catch (error) {
        setTokenValid(false);
        setMessage(error.response?.data?.error || "Token invalide");
      }
      setLoading(false);
    };

    if (token) {
      validateToken();
    } else {
      setLoading(false);
      setTokenValid(false);
    }
  }, [token]);

  useEffect(() => {
    if (newPassword && confirmPassword) {
      setPasswordMatch(newPassword === confirmPassword);
    }
  }, [newPassword, confirmPassword]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleInitializeAccount = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setPasswordMatch(false);
      return;
    }

    try {
      const response = await api.post("/events/initializeAccount", {
        initToken: token,
        newPassword: newPassword,
      });

      if (response.data.success) {
        setMessage("Compte initialisé avec succès ! Redirection vers la page de connexion...");
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setMessage(response.data.error || "Erreur lors de l'initialisation");
      }
    } catch (error) {
      setMessage(error.response?.data?.error || "Une erreur est survenue");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!tokenValid) {
    return (
      <AuthCard>
        <Typography variant="h5" align="center" color="error" gutterBottom>
          Lien invalide
        </Typography>
        <Typography align="center" color="text.secondary" sx={{ mb: 3 }}>
          {message}
        </Typography>
        <Button variant="contained" fullWidth onClick={() => navigate("/login")}>
          Retour à la connexion
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
        <Typography variant="h5" align="center" gutterBottom>
          Initialisation de votre compte
        </Typography>
        <Typography align="center" color="text.secondary" sx={{ mb: 3 }}>
          Bienvenue ! Veuillez créer votre mot de passe pour finaliser l'activation de votre compte : {userEmail}
        </Typography>
        
        <form onSubmit={handleInitializeAccount}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                name="NewPassword"
                type={showPassword ? "text" : "password"}
                label="Nouveau mot de passe"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="ConfirmPassword"
                type={showPassword ? "text" : "password"}
                label="Confirmer le mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            {!passwordMatch && (
              <Grid item xs={12}>
                <Typography color="error" variant="body2">
                  Les mots de passe ne correspondent pas.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                fullWidth
                disabled={!passwordMatch || !newPassword || !confirmPassword}
              >
                Initialiser mon compte
              </Button>
            </Grid>
          </Grid>
        </form>
        
        {message && (
          <Typography
            align="center"
            color={message.includes("succès") ? "success.main" : "error"}
            sx={{ mt: 2 }}
          >
            {message}
          </Typography>
        )}
    </AuthCard>
  );
}

export default AccountInit;